import { useTranslation } from 'react-i18next';
import { Save, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';

import {
  useTenantOrganization,
  useUpdateTenantOrganization
} from '@/pages/admin/api/queries/useTenantOrganization';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Switch,
  Button
} from '@bliks-io/ui';
import { enforcesSeatLimits, type TenantSummary } from '@bliks-io/contracts';
import { TenantSeatLimitsCard } from './TenantSeatLimitsCard';
import { TenantProvisioningCard } from './TenantProvisioningCard';

interface TenantSettingsTabProps {
  tenantId: string;
  tenant: TenantSummary;
}

export function TenantSettingsTab({ tenantId, tenant }: TenantSettingsTabProps) {
  const { t } = useTranslation();
  const { data: org, isLoading } = useTenantOrganization(tenantId);
  const updateOrg = useUpdateTenantOrganization(tenantId);

  const [enableAIOverride, setEnableAIOverride] = useState<boolean | null>(null);

  const serverValue = org?.settings?.enableAI || false;
  const enableAI = enableAIOverride ?? serverValue;
  const hasChanges = enableAIOverride !== null && enableAIOverride !== serverValue;

  const handleSave = async () => {
    try {
      await updateOrg.mutateAsync({
        settings: { enableAI }
      });
      setEnableAIOverride(null);
      toast.success(t('adminSettings.tenant.saved'));
    } catch {
      toast.error(t('adminSettings.tenant.saveFailed'));
    }
  };

  // The seat-limits card writes the TENANT and stays out of the organization
  // read's loading gate, so a slow or failed organization fetch cannot withhold
  // a section that does not depend on it. It leads the tab so that the trailing
  // Save button reads as belonging to the AI card it actually saves — the seat
  // card carries its own.
  return (
    <div className="space-y-6">
      {enforcesSeatLimits(tenant.tenantType) && <TenantSeatLimitsCard tenant={tenant} />}
      {tenant.tenantType === 'partner' && <TenantProvisioningCard tenant={tenant} />}

      {isLoading ? (
        <div className="h-32 bg-muted animate-pulse rounded-lg" />
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Sparkles className="w-5 h-5 text-muted-foreground" />
                {t('adminSettings.tenant.aiTitle')}
              </CardTitle>
              <CardDescription>{t('adminSettings.tenant.aiDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">
                    {t('adminSettings.tenant.enableAILabel')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('adminSettings.tenant.enableAIDescription')}
                  </p>
                </div>
                <Switch checked={enableAI} onCheckedChange={setEnableAIOverride} />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSave} loading={updateOrg.isPending} disabled={!hasChanges}>
              <Save className="w-4 h-4 mr-2" />
              {t('common.Save')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
